import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, authedUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionCard } from "@/components/Primitives";
import LinkedDocsTab from "@/components/LinkedDocsTab";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Paperclip, Download, Trash2, ImageIcon, FileText, Video, Calendar, Plus, Pencil, X, Check, Building2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  bug:                 { label: "Bug",               color: "bg-red-500/15 text-red-500" },
  minor_improvement:   { label: "Minor Improvement", color: "bg-amber-500/15 text-amber-500" },
  major_improvement:   { label: "Major Improvement", color: "bg-purple-500/15 text-purple-500" },
  new_functionality:   { label: "New Functionality", color: "bg-emerald-500/15 text-emerald-500" },
};

// Reused verbatim from TaskDetail for a consistent look.
function AttachmentTile({ att, onDelete, canDelete }) {
  const url = authedUrl(att.url);
  const Icon = att.kind === "image" ? ImageIcon : att.kind === "video" ? Video : FileText;
  const isViewable = att.kind === "image" || att.kind === "video" || (att.mime || "").startsWith("application/pdf");
  const openUrl = isViewable ? url : null;
  return (
    <div data-testid={`attach-${att.id}`} className="group rounded-xl hairline overflow-hidden bg-accent/20 flex flex-col">
      <a
        href={openUrl || url}
        target="_blank"
        rel="noreferrer"
        className={`aspect-video bg-black/20 flex items-center justify-center overflow-hidden ${isViewable ? "cursor-zoom-in" : "cursor-pointer"}`}
        title={isViewable ? "Click to view in new tab" : "Click to download"}
        data-testid={`view-${att.id}`}
      >
        {att.kind === "image" ? (
          <img src={url} alt={att.filename} className="w-full h-full object-cover" />
        ) : att.kind === "video" ? (
          <video src={url} className="w-full h-full object-cover" />
        ) : (
          <Icon className="w-10 h-10 text-muted-foreground" />
        )}
      </a>
      <div className="p-2 flex items-center gap-2">
        <a href={url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 hover:text-primary" data-testid={`open-${att.id}`}>
          <div className="text-xs font-medium truncate">{att.filename}</div>
          <div className="text-[10px] text-muted-foreground">{Math.round(att.size / 1024)} KB · {att.mime}</div>
        </a>
        <a href={url} target="_blank" rel="noreferrer" download={att.filename} data-testid={`dl-${att.id}`} className="p-1.5 rounded hover:bg-accent" title="Download">
          <Download className="w-3.5 h-3.5" />
        </a>
        {canDelete && (
          <button onClick={() => onDelete(att.id)} data-testid={`del-attach-${att.id}`} className="p-1.5 rounded hover:bg-red-500/10 text-red-500">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function TodoList({ ticket, canEdit, canToggle, onChange }) {
  const [newTitle, setNewTitle] = useState("");
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [busy, setBusy] = useState(false);
  const todos = ticket.todos || [];
  const done = todos.filter((t) => t.is_completed).length;
  const total = todos.length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const toggle = async (todo) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/tickets/${ticket.id}/todos/${todo.id}`, { is_completed: !todo.is_completed });
      onChange(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const add = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/todos`, { title });
      onChange(data);
      setNewTitle("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const rename = async (todo) => {
    const title = editValue.trim();
    if (!title) { setEditing(null); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/tickets/${ticket.id}/todos/${todo.id}`, { title });
      onChange(data);
      setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (todo) => {
    if (!window.confirm("Delete this todo?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/tickets/${ticket.id}/todos/${todo.id}`);
      onChange(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <SectionCard
      title={`Checklist · ${done}/${total}`}
      subtitle="Ticket status is driven by these checklist items"
    >
      <div className="space-y-3" data-testid="todo-section">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden" data-testid="progress-bar">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-medium tabular-nums" data-testid="progress-percent">{pct}%</span>
          </div>
          <div className="text-xs text-muted-foreground">{done} of {total} completed</div>
        </div>

        <div className="space-y-1.5">
          {todos.map((td) => (
            <div key={td.id} data-testid={`todo-${td.id}`} className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent/50">
              <input
                type="checkbox"
                data-testid={`todo-check-${td.id}`}
                checked={!!td.is_completed}
                disabled={!canToggle || busy}
                onChange={() => toggle(td)}
                className="w-4 h-4 rounded border-border accent-primary cursor-pointer disabled:cursor-not-allowed"
              />
              {editing === td.id ? (
                <>
                  <Input
                    autoFocus
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") rename(td); if (e.key === "Escape") setEditing(null); }}
                    className="h-7 text-sm"
                    data-testid={`todo-edit-input-${td.id}`}
                  />
                  <Button size="icon" variant="ghost" onClick={() => rename(td)} className="h-7 w-7"><Check className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setEditing(null)} className="h-7 w-7"><X className="w-4 h-4" /></Button>
                </>
              ) : (
                <>
                  <span
                    className={`flex-1 text-sm ${td.is_completed ? "line-through text-muted-foreground" : ""}`}
                    data-testid={`todo-title-${td.id}`}
                  >
                    {td.title}
                  </span>
                  {canEdit && (
                    <>
                      <button
                        onClick={() => { setEditing(td.id); setEditValue(td.title); }}
                        data-testid={`todo-edit-${td.id}`}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-accent"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => remove(td)}
                        data-testid={`todo-del-${td.id}`}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/10 text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {canEdit && (
          <div className="flex items-center gap-2 pt-2 border-t border-border/40">
            <Input
              placeholder="Add a checklist item…"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              className="h-8 text-sm"
              data-testid="todo-new-input"
            />
            <Button size="sm" onClick={add} disabled={!newTitle.trim() || busy} data-testid="todo-add-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/tickets/${id}`).then((r) => setTicket(r.data));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/tickets/${id}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAttachment = async (attId) => {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await api.delete(`/tickets/${id}/attachments/${attId}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!ticket) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const isAdmin = user?.role === "admin";
  const isCreator = ticket.created_by === user?.id;
  const isAssignee = ticket.assignee_id === user?.id;
  const type = TYPE_META[ticket.type] || {};

  return (
    <div className="space-y-6 max-w-5xl">
      <Button data-testid="back-btn" variant="ghost" size="sm" onClick={() => nav("/tickets")}>
        <ArrowLeft className="w-4 h-4 mr-1" />Back to tickets
      </Button>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <Building2 className="w-3 h-3" />{ticket.client_name || "—"} · {ticket.project_name || "—"} <span className="opacity-60">({ticket.project_code})</span>
            </div>
            <h1 data-testid="ticket-title-h1" className="font-display font-semibold text-3xl tracking-tighter mt-1">
              {ticket.title || ticket.ticket_number}
            </h1>
            {ticket.title && (
              <div className="text-xs text-muted-foreground font-mono mt-0.5" data-testid="ticket-number-sub">
                {ticket.ticket_number}
              </div>
            )}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Badge variant="secondary" className="capitalize" data-testid="ticket-status-badge">{ticket.status.replace("_", " ")}</Badge>
              <Badge className={`${type.color || ""} border-0`}>{type.label || ticket.type}</Badge>
              <Badge variant="outline" className="gap-1"><Calendar className="w-3 h-3" />{(ticket.created_at || "").slice(0, 10)}</Badge>
              <Badge variant="outline" className="gap-1" title="CRN">CRN · {ticket.crn}</Badge>
              <Badge
                variant="outline"
                className={`gap-1 ${(ticket.reopen_count || 0) >= 1 ? "border-orange-400/60 text-orange-600 dark:text-orange-300 bg-orange-100/50 dark:bg-orange-950/40" : ""}`}
                data-testid="reopen-count-badge"
                title="Rework / reopen count"
              >
                <span aria-hidden>🔁</span>
                <span className="tabular-nums">{ticket.reopen_count || 0}</span>
              </Badge>
            </div>
          </div>
          <div className="flex flex-col gap-3 text-sm min-w-[220px]">
            {ticket.assignee_name && (
              <div className="flex items-center gap-2">
                <div className="text-[10px] uppercase text-muted-foreground w-20">Assignee</div>
                <Avatar className="h-6 w-6"><AvatarImage src={authedUrl(ticket.assignee_avatar_url)} /><AvatarFallback className="text-[9px]">{ticket.assignee_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                <span className="text-sm">{ticket.assignee_name}</span>
              </div>
            )}
            {ticket.created_by_name && (
              <div className="flex items-center gap-2">
                <div className="text-[10px] uppercase text-muted-foreground w-20">Reporter</div>
                <Avatar className="h-6 w-6"><AvatarFallback className="text-[9px]">{ticket.created_by_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                <span className="text-sm">
                  {ticket.created_by_name}
                  {ticket.created_by_role === "client" && <span className="text-[10px] text-muted-foreground"> · client</span>}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SectionCard title="Details">
            <div className="space-y-4 text-sm">
              {ticket.description ? (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Description</div>
                  <div className="rich-editor-body prose prose-sm dark:prose-invert max-w-none leading-relaxed" data-testid="td-desc-view" dangerouslySetInnerHTML={{ __html: ticket.description }} />
                </div>
              ) : (
                <div className="text-muted-foreground italic" data-testid="td-desc-view">No description provided.</div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-border/40">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Type</div>
                  <div className="capitalize mt-0.5" data-testid="td-type-view">{type.label || ticket.type}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">CRN</div>
                  <div className="mt-0.5">{ticket.crn}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Client</div>
                  <div className="mt-0.5">{ticket.client_name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Created</div>
                  <div className="mt-0.5 tabular-nums">{(ticket.created_at || "").slice(0, 10)}</div>
                </div>
              </div>
            </div>
          </SectionCard>

          <TodoList
            ticket={ticket}
            canEdit={isAdmin || isCreator || isAssignee}
            canToggle={isAdmin || isCreator || isAssignee}
            onChange={(updatedTicket) => setTicket(updatedTicket)}
          />
        </div>

        <div className="space-y-6">
          <SectionCard
            title={`Attachments (${(ticket.attachments || []).length})`}
            subtitle="Images, videos, documents — up to 25 MB each"
            actions={
              <>
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,text/plain" />
                <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-attach-btn">
                  <Paperclip className="w-4 h-4 mr-1" />{uploading ? "Uploading…" : "Upload"}
                </Button>
              </>
            }
          >
            {(ticket.attachments || []).length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8">No attachments yet. Add screenshots, docs, or design files.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(ticket.attachments || []).map((a) => (
                  <AttachmentTile
                    key={a.id}
                    att={a}
                    onDelete={removeAttachment}
                    canDelete={isAdmin || isCreator || a.uploaded_by === user?.id}
                  />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Documentation" subtitle="Technical analysis, root cause, solution notes">
            <LinkedDocsTab entityType="cr" entityId={ticket.id} title="Linked docs" />
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
